import { useRef, useCallback, useEffect } from "react";

/**
 * useWorker — manages a Web Worker lifecycle for pixel processing.
 * Returns { workerRef, processInWorker }.
 *
 * On mount, creates the worker. On unmount, terminates it.
 * Falls back to null (sync processing) if worker creation fails.
 */
export function useWorker() {
  const workerRef = useRef(null);
  const callbacksRef = useRef({});
  const idRef = useRef(0);

  useEffect(() => {
    try {
      const w = new Worker(new URL('../imageWorker.js', import.meta.url));
      w.onmessage = (e) => {
        const { id, resultBuffer } = e.data;
        const cb = callbacksRef.current[id];
        if (cb) {
          delete callbacksRef.current[id];
          cb(resultBuffer);
        }
      };
      workerRef.current = w;
    } catch (err) {
      console.warn('Web Worker creation failed, using sync fallback:', err.message);
    }
    return () => { workerRef.current?.terminate(); };
  }, []);

  const processInWorker = useCallback((msg) => {
    return new Promise((resolve) => {
      const worker = workerRef.current;
      if (!worker) { resolve(null); return; }
      const id = ++idRef.current;
      callbacksRef.current[id] = resolve;
      const transfers = [];
      if (msg.data.baseBuffer) transfers.push(msg.data.baseBuffer);
      if (msg.data.overlayBuffer) transfers.push(msg.data.overlayBuffer);
      if (msg.data.buffer) transfers.push(msg.data.buffer);
      worker.postMessage({ id, ...msg }, transfers);
    });
  }, []);

  return { workerRef, processInWorker };
}
